import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import { QueryRef } from 'apollo-angular';

import { AuthService } from '@core/services/auth/auth.service';
import { NavigationMenuItem, NavigationService } from '@core/services/navigation.service';
import { MeGQL, MeQuery } from '@generated/graphql';
import {
  PalantirSideMenuComponent,
  SideMenuItem,
  SideMenuIaraCredits,
  SideMenuUser,
  SideMenuCompany,
} from '@flux/design-system';
import { MySpaceBadgesService } from '@pages/my-space/my-space-badges.service';
import { IaraCreditsService } from '../../../features/iara-credits/services/iara-credits.service';
import { SidebarService } from './sidebar.service';
import { PermissionsService } from '../../../core/services/permissions.service.js';
import { FeatureFlagService } from '../../../core/services/feature-flag/feature-flag.service.js';
import { FeatureFlagsEnum } from '../../../../generated/featureFlags.enum.js';
import { PermissionsEnum } from '../../../../generated/permissions.enum.js';
import { BlogNotificationService } from '../../../core/services/blog-notification/blog-notification.service.js';
import { sharedLogger } from '../../logger.instance.js';
import { DelegationIndicatorComponent } from '../../components/delegation-indicator/delegation-indicator.component.js';
import { ColorModeService } from '../../../core/services/color-mode.service';

const sidebarLogger = sharedLogger.extend('sidebar');

@Component({
  selector: 'fx-sidebar',
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PalantirSideMenuComponent, DelegationIndicatorComponent],
  providers: [SidebarService],
})
export class SidebarComponent implements OnInit, OnDestroy {
  public readonly blogNotificationService = inject(BlogNotificationService);
  private readonly colorModeService = inject(ColorModeService);

  private readonly user = signal<MeQuery['me'] | null>(null);
  private readonly notificationsCount = signal(0);
  private queryRef!: QueryRef<MeQuery>;

  public isCollapsed = this.sidebarService.collapsed;

  /**
   * Color mode atual ('light' | 'dark') — usado pelo palantir-sidemenu
   * pra renderizar o botão de toggle no footer.
   */
  public colorMode = this.colorModeService.mode;

  /**
   * User info for the side menu
   */
  public sideMenuUser = computed<SideMenuUser>(() => {
    const user = this.user();
    return {
      name: user?.name ?? '',
      email: user?.email ?? '',
      image: user?.avatarUrl ?? 'assets/favicon.svg',
    };
  });

  /**
   * Company info for the side menu (hardcoded to Flux Control)
   */
  public sideMenuCompany = computed<SideMenuCompany>(() => ({
    name: '',
    image: {
      expanded: 'assets/logo/flux-logo.svg',
      collapsed: 'assets/favicon.svg',
    },
  }));

  /**
   * Notification count for the side menu badge
   */
  public notificationCount = computed(() => this.notificationsCount());

  /**
   * Menu items filtered by permissions and feature flags, with badges applied
   */
  public menuItems = computed<SideMenuItem[]>(() => {
    const navItems = this.navigationService.getNavigationItems();
    const notificationCount = this.notificationsCount();
    const hasUnreadBadges = this.mySpaceBadgesService.hasUnreadBadges();
    // Subscreve no signal de flags para que a computed reaja quando o fetch
    // inicial popular o `featureFlagMap` (que é uma Map imperativa, não-reativa).
    this.featureFlagsService.featureFlags();

    return this.processNavigationItems(navItems, notificationCount, hasUnreadBadges);
  });

  /**
   * Widget de saldo de créditos da Iara no rodapé do menu. `null` quando a conta não
   * tem créditos configurados (ou ainda não carregou) — nesse caso o widget não é renderizado.
   */
  public iaraCredits = computed<SideMenuIaraCredits | null>(() => {
    const balance = this.iaraCreditsService.balance();
    if (!balance) {
      return null;
    }

    return {
      percent: Math.min(100, Math.round(balance.percent)),
      consumedLabel: this.formatCredits(balance.consumed),
      totalLabel: this.formatCredits(balance.maxLimit),
      renewalLabel: this.formatRenewalLabel(balance.periodEnd),
    };
  });

  public canShowBlogNotifications = signal(false);

  constructor(
    private readonly sidebarService: SidebarService,
    private readonly authService: AuthService,
    private readonly destroyRef: DestroyRef,
    private readonly navigationService: NavigationService,
    private readonly permissionsService: PermissionsService,
    private readonly featureFlagsService: FeatureFlagService,
    private readonly mySpaceBadgesService: MySpaceBadgesService,
    private readonly meGQL: MeGQL,
    private readonly router: Router,
    private readonly iaraCreditsService: IaraCreditsService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.sidebarService.notificationsCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        sidebarLogger.debug('Received notifications count update:', value);
        this.notificationsCount.set(value);
      });

    this.fetchUser();

    this.canShowBlogNotifications.set(
      await this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagsEnum.FeatureBlogPosts),
    );

    // O saldo de créditos da Iara é carregado na inicialização da aplicação pelo próprio
    // `IaraCreditsService` (auto-bootstrap via `IaraCreditsService.provider()` no app.config),
    // gateado por permissão + feature flag. O widget aqui apenas lê o signal `iaraCredits`.
  }

  ngOnDestroy(): void {
    if (this.queryRef) {
      this.queryRef.stopPolling();
    }
  }

  /**
   * Handle menu item click - navigate to the item's URL
   */
  public onMenuItemClick(item: SideMenuItem): void {
    if (item.url) {
      sidebarLogger.debug('Navigating to menu item:', item.url);
      this.router.navigate([item.url]);
    }
  }

  /**
   * Handle user action click (logout, profile)
   */
  public onUserAction(action: string): void {
    sidebarLogger.debug('User action triggered:', action);
    switch (action) {
      case 'logout':
        this.authService.logout();
        break;
      case 'profile':
        this.router.navigate(['perfil']);
        break;
    }
  }

  public onHelpButtonClick(): void {
    window.open('/wiki/index.html', '_blank');
  }

  /**
   * Clique no widget de créditos da Iara - navega para a tela de consumo de IA.
   */
  public onIaraCreditsClick(): void {
    this.router.navigate(['gerenciar/consumo-ia']);
  }

  /** Formata um valor de crédito (string decimal) como inteiro pt-BR (ex.: "1.850"). */
  private formatCredits(value: string): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return value;
    }
    return Math.round(parsed).toLocaleString('pt-BR');
  }

  /** Monta o rótulo de renovação ("renova em N dias") a partir do fim do ciclo. */
  private formatRenewalLabel(periodEnd: string): string {
    const end = new Date(periodEnd);
    if (Number.isNaN(end.getTime())) {
      return '';
    }

    const millisPerDay = 1000 * 60 * 60 * 24;
    const days = Math.max(0, Math.ceil((end.getTime() - Date.now()) / millisPerDay));

    if (days === 0) {
      return 'renova hoje';
    }
    if (days === 1) {
      return 'renova amanhã';
    }
    return `renova em ${days} dias`;
  }

  public onNotificationButtonClick(): void {
    this.router.navigate(['notificacoes']);
  }

  public onRocketButtonClick(): void {
    if (!this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagsEnum.FeatureBlogPosts)) {
      sidebarLogger.warn('Blog posts feature is disabled. Cannot open blog notifications.');
      return;
    }
    this.blogNotificationService.openManuallyWithAllPosts();
  }

  public onColorModeToggle(): void {
    this.colorModeService.toggle();
  }

  /**
   * Toggle sidebar collapsed state
   */
  public onToggleMenu(): void {
    this.sidebarService.toggleCollapse();
  }

  /**
   * Recursively process navigation items: filter by permissions and apply badges
   */
  private processNavigationItems(
    items: NavigationMenuItem[],
    notificationCount: number,
    hasUnreadBadges: boolean,
  ): SideMenuItem[] {
    return items.map((item) => this.transformItem(item, notificationCount, hasUnreadBadges));
  }

  /**
   * Visibilidade do item: AND entre flags e permissões. Todas as flags
   * precisam estar ativas; basta UMA das `required_permissions` para liberar
   * o item — o array representa alternativas equivalentes (ex.: `*ReadAll`
   * OU `*ReadLimited`, que são mutuamente exclusivas por papel).
   *
   * `isFeatureFlagEnabled` retorna Promise (sempre truthy), por isso leio o
   * `featureFlagMap` síncrono já populado no init.
   */
  private isItemVisible(item: NavigationMenuItem): boolean {
    if (item.has_feature_flag?.length) {
      const allFlagsEnabled = item.has_feature_flag.every(
        (flag) =>
          this.featureFlagsService.featureFlagMap.get(flag as FeatureFlagsEnum)?.active ?? false,
      );
      if (!allFlagsEnabled) {
        return false;
      }
    }

    if (item.required_permissions?.length) {
      const hasAnyPermission = item.required_permissions.some((permission) =>
        this.permissionsService.hasPermission(permission as PermissionsEnum),
      );
      if (!hasAnyPermission) {
        return false;
      }
    }

    return true;
  }

  /**
   * Transform a NavigationMenuItem to SideMenuItem, handling children recursively
   */
  private transformItem(
    item: NavigationMenuItem,
    notificationCount: number,
    hasUnreadBadges: boolean,
  ): SideMenuItem {
    const menuItem: SideMenuItem = {
      id: item.id,
      title: item.title,
      url: item.url,
      hidden: !this.isItemVisible(item),
      icon: item.icon,
      iconVariant: item.iconVariant,
      withDivider: item.withDivider,
      dontAppearOnClosed: item.dontAppearOnClosed,
      isBeta: item.isBeta,
    };

    if (item.children?.length) {
      const filteredChildren = this.processNavigationItems(
        item.children,
        notificationCount,
        hasUnreadBadges,
      );
      // Only include children array if there are visible items
      if (filteredChildren.length > 0) {
        menuItem.children = filteredChildren;
      }
    }

    if (item.id === 'notificacoes' && notificationCount > 0) {
      menuItem.badge = {
        title: notificationCount > 99 ? '99+' : notificationCount,
        bg: 'var(--palantir-color-primary-fill-default)',
      };
    }

    if (item.id === 'meu-espaco' && hasUnreadBadges) {
      menuItem.badge = {
        dot: true,
        title: 'Novidades',
        bg: 'var(--palantir-color-primary-fill-default)',
      };
    }

    return menuItem;
  }

  private fetchUser(): void {
    this.queryRef = this.meGQL.watch();

    this.queryRef.valueChanges
      .pipe(
        map((result) => result.data.me),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.user.set(data);
      });
  }
}
